import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeftOutlined } from '@ant-design/icons'

/**
 * The frame every signed-out screen shares.
 *
 * Sign in, create an account, and the two halves of a password reset are one
 * set, so they get one layout: the mark, a title that says where you are, a
 * line saying what to do, and the form beneath. Without it each screen drifts
 * into its own spacing and the flow reads as four unrelated pages.
 */
export default function AuthShell({
  title, lede, children, foot, as = 'div', onSubmit,
}: {
  title: string
  lede?: ReactNode
  children: ReactNode
  foot?: ReactNode
  as?: 'div' | 'form'
  onSubmit?: (e: React.FormEvent) => void
}) {
  const { t } = useTranslation()
  const inner = (
    <>
      <div className="auth-brand">
        <span className="brand-mark" aria-hidden>A</span>
        <div>
          {/* one account, two products: the name is shared, the line says which */}
          <h1>{t('brand')}</h1>
          <p>{t('brandLine')}</p>
        </div>
      </div>
      <h2 className="auth-title">{title}</h2>
      {lede && <p className="auth-lede">{lede}</p>}
      {children}
      {foot && <div className="auth-foot">{foot}</div>}
    </>
  )
  return (
    <div className="auth">
      {as === 'form'
        ? <form className="auth-card" onSubmit={onSubmit} noValidate>{inner}</form>
        : <div className="auth-card">{inner}</div>}
    </div>
  )
}

/** One labelled input, with the hint and error the field itself owns. */
export function AuthField({ id, label, hint, error, value, onChange, children, ...rest }: {
  id: string
  label: string
  hint?: string
  error?: string
  value: string
  onChange: (v: string) => void
  children?: ReactNode
} & Omit<React.ComponentPropsWithoutRef<'input'>, 'id' | 'value' | 'onChange'>) {
  return (
    <div className={`auth-field${error ? ' has-error' : ''}`}>
      <label htmlFor={id}>{label}</label>
      {children
        ? <div className="input-wrap">
            <input id={id} value={value} onChange={e => onChange(e.target.value)}
              aria-invalid={!!error} {...rest} />
            {children}
          </div>
        : <input id={id} value={value} onChange={e => onChange(e.target.value)}
            aria-invalid={!!error} {...rest} />}
      {error ? <span className="field-error">{error}</span>
             : hint && <span className="field-hint">{hint}</span>}
    </div>
  )
}

/**
 * Going back to sign in.
 *
 * Previously a `.chip` — the status pill used for "EN" and "+2.4%" — which
 * gave a 23-character Mongolian sentence three pixels of padding, no cursor
 * and no hover. This is a secondary navigation action, so it reads as one.
 */
export function BackToSignIn({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation()
  return (
    <button type="button" className="auth-back" onClick={onClick}>
      <ArrowLeftOutlined aria-hidden />
      {t('backToSignIn')}
    </button>
  )
}
